/**
 * Adaptive performance mode.
 *
 * The site leans on glassmorphism (`backdrop-filter: blur`), large decorative
 * blurs and always-on animations. These are nearly free on the GPU but very
 * expensive on the CPU compositor, so when a browser has hardware acceleration
 * OFF (Chrome falls back to a software GL renderer such as SwiftShader), or the
 * device is simply too weak, the UI drops frames and feels laggy.
 *
 * This module decides ONCE whether to run in "lite" mode and toggles the
 * `perf-lite` class on <html>. The CSS under `html.perf-lite` (in index.css)
 * strips backdrop-filter, hides the heavy blurs and pauses ambient animations.
 * Capable devices never get the class and keep the full glass design.
 *
 * Signals, in priority order:
 *   1. `?perf=lite` / `?perf=full` query override (persisted — for testing/support).
 *   2. A persisted decision (already applied before first paint by the inline
 *      script in index.html, so repeat visits never flash the expensive version).
 *   3. Software GL renderer → CPU compositing → lite (deterministic, cached).
 *   4. A short real-FPS probe after boot settles → lite for this session only
 *      (NOT persisted, so a transient boot-time stall can't lock a fast device
 *      into lite forever).
 *
 * Mirror any storage-key / class-name change with the inline script in
 * index.html.
 */

export type PerfMode = 'lite' | 'full';

const STORAGE_KEY = 'safaqat24.perf.mode';
const CLASS = 'perf-lite';

/** Toggle the DOM class only (no persistence). */
function apply(mode: PerfMode): void {
  document.documentElement.classList.toggle(CLASS, mode === 'lite');
}

function getStored(): PerfMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'lite' || v === 'full' ? v : null;
  } catch {
    return null;
  }
}

/** Persist + apply a mode. Exposed for a future settings toggle. */
export function setPerfMode(mode: PerfMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* localStorage blocked — apply for this session only */
  }
  apply(mode);
}

export function getPerfMode(): PerfMode {
  return document.documentElement.classList.contains(CLASS) ? 'lite' : 'full';
}

/**
 * True when WebGL is backed by a software rasteriser (SwiftShader / llvmpipe /
 * Microsoft Basic Render) or is missing entirely — i.e. no GPU acceleration,
 * which is exactly the "hardware acceleration off" case.
 */
function isSoftwareRenderer(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return true; // no WebGL context → almost certainly no GPU accel
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = ext
      ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '')
      : '';
    return /swiftshader|llvmpipe|software|basic render|microsoft basic/i.test(renderer);
  } catch {
    return false; // masked/blocked renderer info — fall through to the FPS probe
  }
}

/** Average frames-per-second over `sampleMs` of requestAnimationFrame ticks. */
function sampleFps(sampleMs: number): Promise<number> {
  return new Promise((resolve) => {
    let frames = 0;
    let start = 0;
    const tick = (t: number) => {
      if (!start) start = t;
      frames += 1;
      const elapsed = t - start;
      if (elapsed >= sampleMs) resolve((frames * 1000) / elapsed);
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function readOverride(): PerfMode | null {
  try {
    const p = new URLSearchParams(window.location.search).get('perf');
    return p === 'lite' || p === 'full' ? p : null;
  } catch {
    return null;
  }
}

/** Decide + apply the performance mode at startup. Safe to call once. */
export function initPerfMode(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  // 1) Explicit override wins and sticks (handy for testing / support).
  const override = readOverride();
  if (override) {
    setPerfMode(override);
    return;
  }

  // 2) A persisted decision was already applied before first paint by the
  //    inline script; honour it and stop (don't re-probe every load).
  const stored = getStored();
  if (stored) {
    apply(stored);
    return;
  }

  // 3) Deterministic, stable signal → cache it.
  if (isSoftwareRenderer()) {
    setPerfMode('lite');
    return;
  }

  // 4) GPU-backed but possibly weak: only downgrade if it actually drops
  //    frames. Measure AFTER boot settles and DON'T persist, so a one-off
  //    boot-time stall can't permanently pin a capable device to lite.
  const probe = async () => {
    const fps = await sampleFps(900);
    if (fps > 0 && fps < 40) apply('lite');
  };
  const schedule = () => {
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    }).requestIdleCallback;
    if (ric) ric(probe, { timeout: 3000 });
    else window.setTimeout(probe, 1800);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}
